// 分组聚合数据准备层
// 职责：从分析项中整理出分组数据，供 Fathom 和普通图层各自渲染表格使用

import { AnalysisItem, LayerGroupType, LegendItem } from "helpers/models";
import groupByLayerIdValueLabel from "report/common/group-layer-value";
import groupLayerDefend from "report/common/group-layer-defend";
import aggregationDetail from "report/common/aggregation-detail";
import aggregationDetailFathom from "report/common/aggregation-detail-fathom";
import transformRule from "report/common/rule";
import sortRowLegend from "report/common/sort-row-legend";
import sortRowFathom from "report/common/sort-row-fathom";
import legendLayerSubtitle from "report/common/legend-layer-subtitle";
import { GroupedHazardData } from "./types";

const DISTANCE_FIELD = "__lineDistanceMeters";

const getLngLatFromPoint = (point: any): [number, number] | null => {
  const lng = Number(point?.longitude ?? point?.lng);
  const lat = Number(point?.latitude ?? point?.lat);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return [lng, lat];
};

const getDistanceBetweenPointsInMeters = (
  previous: [number, number],
  current: [number, number]
) => {
  const earthRadiusInMeters = 6378137;
  const toRadians = (value: number) => (value * Math.PI) / 180;

  const deltaLat = toRadians(current[1] - previous[1]);
  const deltaLng = toRadians(current[0] - previous[0]);
  const lat1 = toRadians(previous[1]);
  const lat2 = toRadians(current[1]);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  return 2 * earthRadiusInMeters * Math.asin(Math.sqrt(a));
};

const getPointSortValue = (item: any, fallbackIndex: number) => {
  const raw = Number(item?.index ?? item?.pointId);
  return Number.isFinite(raw) ? raw : fallbackIndex;
};

const getPointKey = (item: any, fallbackIndex: number) => {
  const indexKey = item?.index ?? item?.pointId;
  if (indexKey !== undefined && indexKey !== null) {
    return `idx:${String(indexKey)}`;
  }

  const coord = getLngLatFromPoint(item);
  if (coord) {
    return `coord:${coord[0]},${coord[1]}`;
  }

  return `fallback:${fallbackIndex}`;
};

const buildLayerPointDistances = (items: AnalysisItem[]) => {
  const byLayer = items.reduce(
    (acc, item, fallbackIndex) => {
      const layerId = item.layerId;
      if (!acc[layerId]) {
        acc[layerId] = [];
      }
      acc[layerId].push({
        item,
        fallbackIndex,
        sortValue: getPointSortValue(item, fallbackIndex),
        key: getPointKey(item, fallbackIndex)
      });
      return acc;
    },
    {} as Record<
      string,
      Array<{
        item: AnalysisItem;
        fallbackIndex: number;
        sortValue: number;
        key: string;
      }>
    >
  );

  const distanceByLayer: Record<string, Record<string, number>> = {};
  const totalByLayer: Record<string, number> = {};

  Object.entries(byLayer).forEach(([layerId, layerItems]) => {
    const uniquePointMap = new Map<
      string,
      { item: AnalysisItem; sortValue: number }
    >();
    layerItems.forEach(({ item, key, sortValue }) => {
      if (!uniquePointMap.has(key)) {
        uniquePointMap.set(key, { item, sortValue });
      }
    });

    const points = Array.from(uniquePointMap.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => a.sortValue - b.sortValue);

    const pointDistanceMap: Record<string, number> = {};
    let totalDistanceInMeters = 0;

    points.forEach((point, index) => {
      if (index === 0) {
        pointDistanceMap[point.key] = 0;
        return;
      }

      const previous = getLngLatFromPoint(points[index - 1].item);
      const current = getLngLatFromPoint(point.item);

      if (!previous || !current) {
        pointDistanceMap[point.key] = 0;
        return;
      }

      const segmentDistance = getDistanceBetweenPointsInMeters(
        previous,
        current
      );
      const validDistance =
        Number.isFinite(segmentDistance) && segmentDistance > 0
          ? segmentDistance
          : 0;

      pointDistanceMap[point.key] = validDistance;
      totalDistanceInMeters += validDistance;
    });

    distanceByLayer[layerId] = pointDistanceMap;
    totalByLayer[layerId] = totalDistanceInMeters;
  });

  return { distanceByLayer, totalByLayer };
};

const sumDistanceByItems = (
  layerId: string,
  items: AnalysisItem[],
  distanceByLayer: Record<string, Record<string, number>>
) => {
  const layerDistanceMap = distanceByLayer[layerId] || {};
  return items.reduce((sum, item, fallbackIndex) => {
    const key = getPointKey(item, fallbackIndex);
    return sum + Number(layerDistanceMap[key] || 0);
  }, 0);
};

/**
 * 准备线性分析的分组数据
 *
 * 这个函数统一了 overview/detail 的数据管道，避免重复过滤和排序
 */
export const buildGroupedData = (
  analysisItems: AnalysisItem[],
  legendItems: LegendItem[],
  locationItems: any[] = [],
  aggregationUnits: string[] = ["numberOfLocations"]
): GroupedHazardData => {
  // 转换规则为内部格式
  const legend: LayerGroupType = transformRule(legendItems);

  // 分离 Fathom 和非 Fathom，分别处理
  const nonFathomItems = analysisItems.filter(
    (e) => !e.layerId.includes("Fathom")
  );
  const fathomItems = analysisItems.filter((e) => e.layerId.includes("Fathom"));

  // 非 Fathom 分组
  const groupData = groupByLayerIdValueLabel(nonFathomItems);
  const groupDetail = aggregationDetail(
    groupData,
    locationItems,
    legend,
    aggregationUnits
  );

  const { distanceByLayer, totalByLayer } =
    buildLayerPointDistances(analysisItems);

  Object.entries(groupData).forEach(([layerId, byLabel]) => {
    Object.entries(byLabel).forEach(([label, labelItems]) => {
      const row = groupDetail[layerId]?.find(
        (item: any) => item.label === label
      );
      if (!row) {
        return;
      }

      row[DISTANCE_FIELD] = sumDistanceByItems(
        layerId,
        labelItems,
        distanceByLayer
      );
    });
  });

  sortRowLegend(groupDetail, legend);

  // Fathom 分组
  const groupedResultFathom = groupLayerDefend(fathomItems);
  const groupDetailFathom = aggregationDetailFathom(
    groupedResultFathom,
    locationItems,
    legend,
    aggregationUnits
  );

  Object.entries(groupedResultFathom).forEach(([layerId, statuses]) => {
    Object.entries(statuses).forEach(([statusKey, labels]) => {
      Object.entries(labels).forEach(([label, labelItems]) => {
        const row = groupDetailFathom[layerId]?.[
          statusKey as "defended" | "undefended"
        ]?.find((item: any) => item.label === label);

        if (!row) {
          return;
        }

        row[DISTANCE_FIELD] = sumDistanceByItems(
          layerId,
          labelItems,
          distanceByLayer
        );
      });
    });
  });

  sortRowFathom(groupDetailFathom);

  // 获取每图层的子标题
  const layerTitle = legendLayerSubtitle(legendItems);
  const maxDistanceInMeters = Math.max(...Object.values(totalByLayer), 0);
  const distanceUnit = maxDistanceInMeters >= 1000 ? "km" : "m";

  return {
    grouped: groupDetail, // 非 Fathom 分组结果
    fathom: groupDetailFathom, // Fathom 分组结果
    legend, // 转换后的规则
    layerTitle, // 图层子标题
    distanceUnit
  };
};
